import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, 
  FaHome,
  FaMousePointer,
  FaDrawPolygon,
  FaDoorOpen,
  FaCircle,
  FaPlay,
  FaExpand,
  FaTrash,
  FaCenterFocusStrong,
  FaBed,
  FaToilet,
  FaTree,
  FaCar,
  FaKeyboard,
  FaCalculator,
  FaEye,
  FaCog,
  FaLayerGroup,
  FaRuler,
  FaPalette,
  FaRedoAlt,
  FaMagnet,
  FaWaveSquare,
  FaSearch,
  FaQuestionCircle,
  FaSquare,
  FaCube,
  FaChevronDown,
  FaChevronRight
} from 'react-icons/fa';
import DoorIcon from './icons/DoorIcon';
import './InstructionsPage.css';

const InstructionsPage = () => {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState('getting-started');
  const [searchQuery, setSearchQuery] = useState('');

  const sections = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: <FaHome />,
      content: [
        {
          title: 'Welcome to Floor Planner Pro',
          description: 'A professional floor planning tool with advanced carpet estimation capabilities.',
          steps: [
            'Start by selecting a drawing tool from the sidebar',
            'Click and drag on the canvas to create shapes with preview',
            'See real-time preview while dragging to define size',
            'Use the select tool to move and resize objects',
            'Add doors, furniture, and fixtures as needed',
            'Generate carpet estimates with detailed analysis'
          ]
        }
      ]
    },
    {
      id: 'drawing-tools',
      title: 'Drawing Tools',
      icon: <FaDrawPolygon />,
      content: [
        {
          title: 'Select Tool',
          icon: <FaMousePointer />,
          description: 'Select, move, and resize shapes on the canvas.',
          shortcut: 'S',
          steps: [
            'Click on any shape to select it',
            'Drag to move the selected shape',
            'Use resize handles to change dimensions',
            'Hold Shift while resizing to maintain proportions',
            'Press Delete to remove selected shapes'
          ]
        },
        {
          title: 'Rectangle Tool',
          icon: <FaDrawPolygon />,
          description: 'Create rectangular rooms and spaces with drag-to-create preview.',
          shortcut: 'R',
          steps: [
            'Click the Rectangle tool in the sidebar',
            'Click and drag on the canvas to see preview',
            'Semi-transparent preview shows final size',
            'Release to complete the rectangle with calculated dimensions',
            'Perfect for rooms, hallways, and regular spaces'
          ]
        },
        {
          title: 'Circle Tool',
          icon: <FaCircle />,
          description: 'Draw circular and curved spaces with preview.',
          shortcut: 'C',
          steps: [
            'Select the Circle tool from the sidebar',
            'Click and drag to preview the diameter',
            'Real-time preview shows final circle size',
            'Release to complete the circle',
            'Great for curved walls and decorative spaces'
          ]
        },
        {
          title: 'Triangle Tool',
          icon: <FaPlay />,
          description: 'Create triangular spaces with drag preview.',
          shortcut: 'T',
          steps: [
            'Choose the Triangle tool',
            'Click and drag to preview the triangle',
            'Preview shows final shape dimensions',
            'Release to complete the triangle',
            'Useful for angled walls and corners'
          ]
        },
        {
          title: 'Line Curves',
          icon: <FaWaveSquare />,
          description: 'Create curved lines for advanced room shapes.',
          shortcut: 'C (when line selected)',
          steps: [
            'Select any line (from rectangles or standalone)',
            'Press "C" key to activate curve mode',
            'Red control point appears for curve adjustment',
            'Drag the red point to shape the curve',
            'Press "C" again to exit curve mode',
            'Curved lines show accurate measurements'
          ]
        }
      ]
    },
    {
      id: 'door-system',
      title: 'Smart Door System',
      icon: <FaDoorOpen />,
      content: [
        {
          title: 'Door Placement',
          description: 'Advanced door system with drag-to-create preview, rotation and snap functionality.',
          shortcut: 'D',
          steps: [
            'Select the Door tool from the sidebar',
            'Click and drag to preview door size',
            'Semi-transparent preview shows final placement',
            'Release to place door with calculated radius',
            'Doors appear as semi-circles with swing indicators',
            'Use the blue rotation handle to adjust orientation',
            'Drag near room walls for automatic snapping'
          ]
        },
        {
          title: 'Door Rotation',
          icon: <FaRedoAlt />,
          description: 'Rotate doors to face the correct direction.',
          steps: [
            'Select a door to see the blue rotation handle',
            'Click and drag the blue circle to rotate',
            'Doors can be rotated to any angle',
            'Rotation affects carpet cutting calculations',
            'Visual swing line shows door opening direction'
          ]
        },
        {
          title: 'Snap to Walls',
          icon: <FaMagnet />,
          description: 'Doors automatically snap to nearby room walls.',
          steps: [
            'Drag a door within 30 pixels of a room wall',
            'Door highlights green when snap is available',
            'Release to snap the door to the wall',
            'Door automatically rotates to face into the room',
            'Snapped doors show in room analysis'
          ]
        },
        {
          title: 'Working with Doors',
          icon: <DoorIcon size={16} />,
          description: 'Add quarter-circle doors with snap confirmation.',
          steps: [
            'Select Door Tool (D) from sidebar',
            'Must have at least one rectangle on canvas first',
            'Click and drag to create a quarter-circle door (0-90 degrees)',
            'Use the bottom line handle (orange square) to scale door radius',
            'Drag door near any rectangle wall to see popup confirmation',
            'Popup asks "Snap to [side] side?" with Yes/No buttons',
            'Click "Yes" to attach door to wall, "No" to place freely',
            'Press F while door is selected to toggle orientation',
            'Click "Flip" button to rotate door 180 degrees instantly',
            'Use rotation handle for precise angle adjustments',
            'Door shows as quarter arc with hinge point and swing line'
          ]
        }
      ]
    },
    {
      id: 'furniture-fixtures',
      title: 'Furniture & Fixtures',
      icon: <FaBed />,
      content: [
        {
          title: 'Furniture Items',
          description: 'Add realistic furniture to your floor plan with drag-to-create preview.',
          steps: [
            'Select any furniture tool from the sidebar',
            'Click and drag to preview the furniture size',
            'Semi-transparent preview shows final placement',
            'Release to place with calculated dimensions',
            'Beds - Perfect for bedroom planning',
            'Chairs & Sofas - Living room furniture',
            'Dining Tables - Kitchen and dining areas',
            'TV Units - Entertainment centers'
          ]
        },
        {
          title: 'Bathroom Fixtures',
          icon: <FaToilet />,
          description: 'Complete bathroom planning tools with preview.',
          steps: [
            'Select fixture tools and drag to preview size',
            'All fixtures support drag-to-create preview',
            'Toilets - Standard bathroom fixtures',
            'Bathtubs - Various sizes available',
            'Position fixtures for optimal layout',
            'Consider plumbing and electrical requirements'
          ]
        },
        {
          title: 'Outdoor Elements',
          icon: <FaTree />,
          description: 'Landscape and exterior planning with preview.',
          steps: [
            'Trees and Plants - Landscaping elements',
            'Vehicles - Garage and driveway planning',
            'All outdoor items support drag-to-create',
            'Preview shows final size before placement',
            'Garden and yard features'
          ]
        }
      ]
    },
    {
      id: 'carpet-estimation',
      title: 'Carpet Estimation',
      icon: <FaCalculator />,
      content: [
        {
          title: 'Advanced Calculations',
          description: 'Professional carpet estimation with detailed analysis.',
          steps: [
            'Automatic area calculation for all rooms',
            'Perimeter measurements for transitions',
            'Door analysis for cutting complexity',
            'Waste percentage calculations',
            'Professional installation factors'
          ]
        },
        {
          title: 'Room Analysis',
          icon: <FaRuler />,
          description: 'Detailed room-by-room breakdown.',
          steps: [
            'Each room analyzed individually',
            'Area calculations in square feet/meters',
            'Perimeter for baseboards and transitions',
            'Door count and complexity factors',
            'Shape-specific cutting strategies'
          ]
        },
        {
          title: 'Cost Estimation',
          icon: <FaCalculator />,
          description: 'Complete cost breakdown and analysis.',
          steps: [
            'Material costs with waste factors',
            'Labor costs for installation',
            'Transition strip requirements',
            'Additional supplies and tools',
            'Total project cost estimation'
          ]
        }
      ]
    },
    {
      id: 'controls-navigation',
      title: 'Controls & Navigation',
      icon: <FaExpand />,
      content: [
        {
          title: 'Canvas Controls',
          description: 'Navigate and control your floor plan view.',
          steps: [
            'Zoom In/Out - Use toolbar buttons or mouse wheel',
            'Pan - Click and drag empty space to move view',
            'Center View - Reset to default position',
            'Fit to Screen - Automatically scale to fit content',
            'Full Screen - Maximize canvas area'
          ]
        },
        {
          title: 'Keyboard Shortcuts',
          icon: <FaKeyboard />,
          description: 'Speed up your workflow with shortcuts.',
          steps: [
            'S - Select Tool (move and resize)',
            'R - Rectangle Tool (draw rooms)',
            'D - Door Tool (place doors)',
            'C - Circle Tool (curved spaces)',
            'T - Triangle Tool (angled rooms)',
            'C - Curve Mode (when line selected)',
            'F - Toggle Door Orientation (when door selected)',
            'Delete - Remove selected objects',
            'Ctrl+Z - Undo last action',
            'Ctrl+Y - Redo action'
          ]
        }
      ]
    },
    {
      id: 'advanced-features',
      title: 'Advanced Features',
      icon: <FaCog />,
      content: [
        {
          title: 'Shape Merging',
          icon: <FaLayerGroup />,
          description: 'Combine multiple shapes into complex rooms.',
          steps: [
            'Select multiple overlapping shapes',
            'Use merge function to combine them',
            'Creates complex room geometries',
            'Maintains accurate area calculations',
            'Useful for L-shaped and irregular rooms'
          ]
        },
        {
          title: 'Visual Feedback',
          icon: <FaEye />,
          description: 'Real-time visual indicators and feedback.',
          steps: [
            'Green highlighting for snap zones',
            'Blue handles for rotation controls',
            'Selection indicators and resize handles',
            'Grid snapping for precise alignment',
            'Color coding for different object types'
          ]
        },
        {
          title: 'Professional Tools',
          icon: <FaWaveSquare />,
          description: 'Industry-standard features for professionals.',
          steps: [
            'Precise measurements and dimensions',
            'Scale-accurate drawings',
            'Professional carpet cutting diagrams',
            'Detailed material lists and specifications',
            'Export capabilities for client presentations'
          ]
        }
      ]
    }
  ];

  // Filter sections and content based on search query
  const filteredSections = useMemo(() => {
    if (!searchQuery.trim()) return sections;

    return sections.map(section => {
      const filteredContent = section.content.filter(item => {
        const searchText = `${section.title} ${item.title} ${item.description} ${item.steps?.join(' ')} ${item.shortcut || ''}`.toLowerCase();
        return searchText.includes(searchQuery.toLowerCase());
      });

      return {
        ...section,
        content: filteredContent,
        isVisible: filteredContent.length > 0 || 
                  section.title.toLowerCase().includes(searchQuery.toLowerCase())
      };
    }).filter(section => section.isVisible);
  }, [sections, searchQuery]);

  const currentSection = filteredSections.find(s => s.id === activeSection) || filteredSections[0];

  return (
    <div className="instructions-page">
      {/* Header */}
      <header className="instructions-header">
        <div className="header-content">
          <button 
            className="back-button"
            onClick={() => navigate('/')}
          >
            <FaArrowLeft />
            <span>Back to Home</span>
          </button>
          
          <div className="header-title">
            <h1>Floor Planner Pro Documentation</h1>
            <p>Complete guide to professional floor planning</p>
          </div>
          
          <button 
            className="start-button"
            onClick={() => navigate('/planner')}
          >
            <FaHome />
            <span>Start Planning</span>
          </button>
        </div>
      </header>

      <div className="instructions-content">
        {/* Sidebar Navigation */}
        <nav className="instructions-nav">
          <div className="nav-title">
            <h3>Documentation</h3>
          </div>
          
          {/* Search functionality */}
          <div className="search-container">
            <div className="search-input-wrapper">
              <FaSearch className="search-icon" />
              <input
                type="text"
                placeholder="Search instructions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-input"
              />
              {searchQuery && (
                <button
                  className="clear-search"
                  onClick={() => setSearchQuery('')}
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            {searchQuery && (
              <div className="search-results-info">
                {filteredSections.length} result{filteredSections.length !== 1 ? 's' : ''} found
              </div>
            )}
          </div>
          
          <ul className="nav-list">
            {filteredSections.map((section) => (
              <li key={section.id}>
                <button
                  className={`nav-item ${activeSection === section.id ? 'active' : ''}`}
                  onClick={() => setActiveSection(section.id)}
                >
                  <span className="nav-icon">{section.icon}</span>
                  <span className="nav-text">{section.title}</span>
                  {searchQuery && section.content.length > 0 && (
                    <span className="content-count">{section.content.length}</span>
                  )}
                </button>
              </li>
            ))}
            {filteredSections.length === 0 && searchQuery && (
              <li className="no-results">
                <div className="no-results-message">
                  No results found for "{searchQuery}"
                  <button onClick={() => setSearchQuery('')} className="reset-search">
                    Clear search
                  </button>
                </div>
              </li>
            )}
          </ul>
        </nav>

        {/* Main Content */}
        <main className="instructions-main">
          {currentSection ? (
            <>
              <div className="section-header">
                <div className="section-icon">
                  {currentSection.icon}
                </div>
                <div className="section-title">
                  <h2>{currentSection.title}</h2>
                  <p>
                    {searchQuery 
                      ? `Search results for "${searchQuery}" in ${currentSection.title}`
                      : 'Detailed instructions and best practices'
                    }
                  </p>
                </div>
              </div>

              <div className="content-sections">
                {currentSection.content.map((subsection, index) => (
                  <div key={index} className="content-card">
                    <div className="card-header">
                      {subsection.icon && (
                        <div className="card-icon">
                          {subsection.icon}
                        </div>
                      )}
                      <div className="card-title">
                        <h3>{subsection.title}</h3>
                        {subsection.shortcut && (
                          <span className="shortcut-badge">
                            Press <kbd>{subsection.shortcut}</kbd>
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <p className="card-description">{subsection.description}</p>
                    
                    <div className="steps-list">
                      <h4>Instructions:</h4>
                      <ol>
                        {subsection.steps.map((step, stepIndex) => (
                          <li key={stepIndex}>{step}</li>
                        ))}
                      </ol>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <div className="no-content">
              <div className="no-content-icon">
                <FaSearch />
              </div>
              <h3>No results found</h3>
              <p>Try adjusting your search terms or browse the categories in the sidebar.</p>
              <button 
                className="clear-search-button"
                onClick={() => setSearchQuery('')}
              >
                Clear Search
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default InstructionsPage; 